import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FaTimes, FaFilter, FaChevronDown, FaCalendarAlt, FaDownload,
  FaEnvelope, FaChartLine,
} from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";
import { getDemoActivity } from "../demo/demoContactDetails";
import { useViewScope } from "../contexts/ViewScopeContext";
import {
  EMAIL_EVENT_CONFIG,
  PLATFORM_EVENT_CONFIG,
  CAMPAIGN_CONFIG,
  CAMPAIGN_OPTIONS,
  EMAIL_EVENT_OPTIONS,
  PLATFORM_EVENT_OPTIONS,
  SMS_EVENT_OPTIONS,
  DATE_RANGE_OPTIONS,
  PAGE_SIZE,
  formatEventBadge,
  formatTimestamp,
  formatTime,
  formatRelative,
  normaliseCampaign,
  campaignLabel,
} from "../pages/ActivityExplorer";

// Modal that surfaces a single contact's activity timeline using the
// same /activity-explorer query the Activity Explorer page uses, but
// locked to one contact (no email search field — the email is fixed).
// Mirrors the Activity Explorer row UI so the experience is consistent.
export default function ContactActivityModal({ contact, onClose }) {
  const open = !!contact;
  const contactEmail = (contact?.email || "").trim().toLowerCase();
  // production.events stores the phone in the `email` column when the lead
  // arrived via SMS, so we pass the phone alongside the email and the
  // backend OR's them when scoping platform events to one contact.
  const contactPhone = (
    contact?.clean_mobile || contact?.mobile || contact?.phone || ""
  ).toString().trim();
  const { viewScope, agentFilter } = useViewScope();

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768,
  );

  const [rows, setRows] = useState([]);
  const [platformEvents, setPlatformEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);

  // Filters
  const [campaign, setCampaign] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  // Default to a longer window than ActivityExplorer's 7d default because
  // we're scoped to one contact — recent activity for a single person is
  // sparse, so 90 days surfaces something useful on first open.
  const [dateRange, setDateRange] = useState("90");
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);

  const dateDropdownRef = useRef(null);
  const eventDropdownRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset filters whenever a different contact is opened so prior state
  // doesn't bleed across users.
  useEffect(() => {
    if (!open) return;
    setCampaign("");
    setEventTypes([]);
    setDateRange("90");
    setRows([]);
    setPlatformEvents([]);
    setTotal(0);
    setHasMore(false);
    setOffset(0);
  }, [contactEmail, open]);

  // Close dropdowns on outside click.
  useEffect(() => {
    function handleClick(e) {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
        setDateDropdownOpen(false);
      }
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(e.target)) {
        setEventDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchData = useCallback(async (newOffset = 0, append = false) => {
    if (!contactEmail) return;
    if (!append) setLoading(true);
    else setLoadingMore(true);
    // Demo mode: serve this contact's sample timeline (the backend is blocked
    // by the demo guard). Apply the modal's filters client-side so the
    // campaign / event-type / date controls still feel live on sample data.
    if (typeof window !== "undefined" && window.__demoMode) {
      const sample = getDemoActivity(contactEmail);
      const cutoffMs = dateRange === "all"
        ? null
        : Date.now() - (parseInt(dateRange, 10) || 90) * 86400 * 1000;
      const withinRange = (r) => cutoffMs === null || new Date(r.created_at).getTime() >= cutoffMs;
      const matchesType = (r) => eventTypes.length === 0 || eventTypes.includes(r.event_type);
      let emailRows = (sample.data || [])
        .filter((r) => (!campaign || r.tag === campaign))
        .filter(matchesType)
        .filter(withinRange);
      const platRows = (sample.platform_events || [])
        // Platform events carry no campaign tag, so a campaign filter hides them.
        .filter(() => !campaign)
        .filter(matchesType)
        .filter(withinRange);
      setRows(emailRows);
      setPlatformEvents(append ? [] : platRows);
      setTotal(emailRows.length);
      setHasMore(false);
      setOffset(0);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    try {
      const body = {
        campaign,
        event_types: eventTypes,
        date_range: dateRange,
        email_search: contactEmail,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        offset: newOffset,
        limit: PAGE_SIZE,
        view_scope: viewScope,
        agent_filter: agentFilter,
      };
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/activity-explorer`, body
      );
      const d = res.data || res;
      const newRows = Array.isArray(d.data) ? d.data : [];
      if (append) {
        setRows(prev => [...prev, ...newRows]);
      } else {
        setRows(newRows);
        setPlatformEvents(Array.isArray(d.platform_events) ? d.platform_events : []);
      }
      setTotal(d.total || 0);
      setHasMore(!!d.has_more);
      setOffset(newOffset);
    } catch (e) {
      if (!append) { setRows([]); setPlatformEvents([]); setTotal(0); setHasMore(false); }
    }
    setLoading(false);
    setLoadingMore(false);
  }, [contactEmail, contactPhone, campaign, eventTypes, dateRange, viewScope, agentFilter]);

  // Refetch on filter / contact change.
  useEffect(() => {
    if (!open) return;
    fetchData(0);
  }, [open, fetchData]);

  const handleShowMore = () => fetchData(offset + PAGE_SIZE, true);

  const handleExportCsv = () => {
    if (!rows.length) return;
    const headers = ["Event", "Recipient", "Subject", "Campaign", "Date & Time"];
    const csvRows = rows.map(r => [
      formatEventBadge(r.event_type, false).label,
      r.email || "",
      (r.subject || "").replace(/"/g, '""'),
      campaignLabel(r.tag),
      r.created_at || "",
    ]);
    const csv = [headers.join(","), ...csvRows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-${contactEmail || "contact"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Merge email + platform events, newest first.
  const allRows = useMemo(() => (
    [
      ...rows.map(r => ({ ...r, _source: "email" })),
      ...platformEvents.map(r => ({ ...r, _source: "platform" })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  ), [rows, platformEvents]);

  const currentDateLabel = DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || dateRange;

  const displayName = useMemo(() => {
    if (!contact) return "";
    const parts = [contact.first_name, contact.last_name].filter(Boolean);
    return parts.join(" ") || contact.name || contactEmail;
  }, [contact, contactEmail]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        // Offset by sidebar width on desktop (see AIConversationModal).
        left: isMobile ? 0 : 260,
        background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: isMobile ? "stretch" : "center",
        padding: isMobile ? 0 : 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#f6f8fa",
          borderRadius: isMobile ? 0 : 16,
          width: "100%",
          maxWidth: isMobile ? "none" : 1100,
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: isMobile ? "14px 16px" : "18px 24px",
          background: "#fff", borderBottom: "1px solid #e5e7eb",
          flexShrink: 0,
        }}>
          <div style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 36, height: 36, borderRadius: 10,
            background: "#f0fdfa", color: "#0f766e",
          }}>
            <FaChartLine />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
              Activity
            </div>
            <div style={{
              fontSize: 12, color: "#6b7280", marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {displayName && displayName !== contactEmail
                ? <>{displayName} · <span style={{ color: "#9ca3af" }}>{contactEmail}</span></>
                : contactEmail}
            </div>
          </div>
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 6,
              color: "#9ca3af", fontSize: 18,
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          padding: isMobile ? "12px 16px 24px" : "16px 24px 24px",
        }}>
          {/* Filter bar */}
          <div style={{
            display: "flex", gap: isMobile ? 6 : 10, marginBottom: isMobile ? 10 : 16,
            flexWrap: "wrap", alignItems: "center",
          }}>
            {/* Campaign filter */}
            <div style={{ position: "relative", minWidth: isMobile ? 140 : 180 }}>
              <FaFilter style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: isMobile ? 10 : 12 }} />
              <select
                value={campaign}
                onChange={e => setCampaign(e.target.value)}
                style={{
                  width: "100%", padding: isMobile ? "7px 10px 7px 28px" : "9px 12px 9px 32px", borderRadius: 8,
                  border: "1px solid #e5e7eb", fontSize: isMobile ? 12 : 13, boxSizing: "border-box",
                  background: "#fff", outline: "none", cursor: "pointer",
                  appearance: "none", WebkitAppearance: "none",
                }}
              >
                {CAMPAIGN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <FaChevronDown style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 10, pointerEvents: "none" }} />
            </div>

            {/* Event type multi-select */}
            <div ref={eventDropdownRef} style={{ position: "relative", minWidth: isMobile ? 150 : 200 }}>
              <button
                onClick={() => setEventDropdownOpen(p => !p)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, width: "100%",
                  padding: isMobile ? "7px 10px" : "9px 12px", borderRadius: 8, border: "1px solid #e5e7eb",
                  fontSize: isMobile ? 12 : 13, background: "#fff", cursor: "pointer", color: "#374151",
                  boxSizing: "border-box",
                }}
              >
                {eventTypes.length === 0
                  ? "All events"
                  : `${eventTypes.length} event${eventTypes.length > 1 ? "s" : ""} selected`}
                {eventTypes.length > 0 && (
                  <FaTimes
                    onClick={e => { e.stopPropagation(); setEventTypes([]); }}
                    style={{ fontSize: 10, color: "#9ca3af", marginLeft: 2, cursor: "pointer" }}
                  />
                )}
                <FaChevronDown style={{ marginLeft: "auto", color: "#9ca3af", fontSize: 10 }} />
              </button>
              {eventDropdownOpen && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, marginTop: 4, minWidth: 240,
                  background: "#fff", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                  border: "1px solid #e5e7eb", zIndex: 100, maxHeight: 360, overflowY: "auto",
                }}>
                  <EventGroup title="Email Events" options={EMAIL_EVENT_OPTIONS}
                    config={EMAIL_EVENT_CONFIG} accent="#047857"
                    eventTypes={eventTypes} setEventTypes={setEventTypes} />
                  <EventGroup title="Platform Events" options={PLATFORM_EVENT_OPTIONS}
                    config={PLATFORM_EVENT_CONFIG} accent="#7c3aed" borderTop
                    eventTypes={eventTypes} setEventTypes={setEventTypes} />
                  <EventGroup title="SMS Events" options={SMS_EVENT_OPTIONS}
                    config={PLATFORM_EVENT_CONFIG} accent="#16a34a" borderTop
                    eventTypes={eventTypes} setEventTypes={setEventTypes} />
                </div>
              )}
            </div>

            {/* Date range */}
            <div ref={dateDropdownRef} style={{ position: "relative", minWidth: isMobile ? 130 : 160 }}>
              <button
                onClick={() => setDateDropdownOpen(p => !p)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, width: "100%",
                  padding: isMobile ? "7px 10px" : "9px 12px", borderRadius: 8, border: "1px solid #e5e7eb",
                  fontSize: isMobile ? 12 : 13, background: "#fff", cursor: "pointer", color: "#374151",
                }}
              >
                <FaCalendarAlt style={{ color: "#9ca3af", fontSize: 12 }} />
                {currentDateLabel}
                <FaChevronDown style={{ marginLeft: "auto", color: "#9ca3af", fontSize: 10 }} />
              </button>
              {dateDropdownOpen && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, marginTop: 4, minWidth: 180,
                  background: "#fff", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                  border: "1px solid #e5e7eb", zIndex: 100, overflow: "hidden",
                }}>
                  {DATE_RANGE_OPTIONS.map(o => (
                    <div
                      key={o.value}
                      onClick={() => { setDateRange(o.value); setDateDropdownOpen(false); }}
                      style={{
                        padding: "10px 16px", fontSize: 13, cursor: "pointer",
                        background: dateRange === o.value ? "#ecfdf5" : "#fff",
                        color: dateRange === o.value ? "#047857" : "#374151",
                        fontWeight: dateRange === o.value ? 600 : 400,
                      }}
                      onMouseEnter={e => { if (dateRange !== o.value) e.currentTarget.style.background = "#f9fafb"; }}
                      onMouseLeave={e => { if (dateRange !== o.value) e.currentTarget.style.background = "#fff"; }}
                    >
                      {o.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Export */}
            <button
              onClick={handleExportCsv}
              disabled={!rows.length}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "9px 14px",
                borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13,
                background: "#fff", cursor: rows.length ? "pointer" : "default",
                color: rows.length ? "#374151" : "#9ca3af",
              }}
            >
              <FaDownload style={{ fontSize: 11 }} />
              Export
            </button>
          </div>

          {/* Summary + table header */}
          {!loading && allRows.length > 0 && (
            <>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>
                {(total + platformEvents.length).toLocaleString()} total event{(total + platformEvents.length) !== 1 ? "s" : ""}
                {rows.length < total ? ` · Showing ${allRows.length}` : ""}
              </div>
              {!isMobile && (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr 130px 160px",
                  padding: "10px 20px", borderBottom: "2px solid #e5e7eb",
                  background: "#fafbfc", fontSize: 11, fontWeight: 600,
                  textTransform: "uppercase", color: "#6b7280", letterSpacing: "0.04em",
                  border: "1px solid #e5e7eb", borderRadius: "12px 12px 0 0",
                }}>
                  <div>Event</div>
                  <div>Detail</div>
                  <div>Tag</div>
                  <div style={{ textAlign: "right" }}>Date &amp; Time</div>
                </div>
              )}
            </>
          )}

          {/* Results */}
          {loading ? (
            <div style={{
              background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px #0001",
              border: "1px solid #e5e7eb", overflow: "hidden",
            }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: isMobile ? "12px 14px" : "12px 20px",
                  borderBottom: i < 5 ? "1px solid #f3f4f6" : "none",
                }}>
                  <Skeleton width={80} height={20} borderRadius={10} />
                  <div style={{ flex: 1 }}><Skeleton width="80%" height={13} /></div>
                  {!isMobile && <Skeleton width={60} height={20} borderRadius={10} />}
                  <Skeleton width={100} height={12} />
                </div>
              ))}
            </div>
          ) : allRows.length === 0 ? (
            <div style={{
              background: "#fff", borderRadius: 12, padding: 40,
              boxShadow: "0 2px 8px #0001", border: "1px solid #e5e7eb", textAlign: "center",
            }}>
              <FaEnvelope style={{ fontSize: 32, color: "#d1d5db", marginBottom: 12 }} />
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: "#1f2937" }}>No Activity Found</h3>
              <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
                Try widening the date range or clearing the filters.
              </p>
            </div>
          ) : (
            <>
              <div style={{
                background: "#fff",
                borderRadius: isMobile ? 12 : "0 0 12px 12px",
                boxShadow: "0 2px 8px #0001",
                borderLeft: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb",
                borderTop: isMobile ? "1px solid #e5e7eb" : "none",
                overflow: "hidden", marginBottom: 16,
              }}>
                {allRows.map((row, idx) => (
                  <ActivityRow
                    key={idx}
                    row={row}
                    isLast={idx === allRows.length - 1}
                    isMobile={isMobile}
                  />
                ))}
              </div>

              {hasMore && (
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <button
                    onClick={handleShowMore}
                    disabled={loadingMore}
                    style={{
                      padding: "10px 28px", borderRadius: 8, border: "1px solid #e5e7eb",
                      background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151",
                      cursor: loadingMore ? "default" : "pointer",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                    {loadingMore ? <Spinner animation="border" size="sm" /> : <>Show more</>}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EventGroup({ title, options, config, accent, eventTypes, setEventTypes, borderTop }) {
  return (
    <>
      <div style={{
        padding: "10px 12px 4px", fontSize: 10, fontWeight: 700, color: "#9ca3af",
        textTransform: "uppercase", letterSpacing: "0.05em",
        borderTop: borderTop ? "1px solid #f3f4f6" : "none",
      }}>{title}</div>
      {options.map(o => {
        const checked = eventTypes.includes(o.value);
        const cfg = config[o.value];
        return (
          <label
            key={o.value}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "7px 12px", cursor: "pointer", fontSize: 13,
              background: checked ? "#f0fdf4" : "#fff",
            }}
            onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "#f9fafb"; }}
            onMouseLeave={e => { e.currentTarget.style.background = checked ? "#f0fdf4" : "#fff"; }}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => setEventTypes(prev => checked ? prev.filter(v => v !== o.value) : [...prev, o.value])}
              style={{ accentColor: accent }}
            />
            {cfg && <cfg.Icon style={{ fontSize: 11, color: cfg.color }} />}
            <span style={{ color: checked ? "#047857" : "#374151", fontWeight: checked ? 600 : 400 }}>{o.label}</span>
          </label>
        );
      })}
    </>
  );
}

function ActivityRow({ row, isLast, isMobile }) {
  const isPlatform = row._source === "platform";
  const isSms = row.event_type === "sms_sent" || row.event_type === "sms_unsubscribe";
  const cfg = formatEventBadge(row.event_type, isPlatform);
  const tag = campaignLabel(row.tag);
  const relative = formatRelative(row.created_at);

  const tagChip = (() => {
    const norm = normaliseCampaign(row.tag);
    const ccfg = CAMPAIGN_CONFIG[norm];
    if (ccfg) {
      return (
        <span style={{
          fontSize: 11, borderRadius: 12, padding: "2px 10px",
          background: ccfg.bg, color: ccfg.color, border: `1px solid ${ccfg.border}`,
          whiteSpace: "nowrap",
        }}>{ccfg.label}</span>
      );
    }
    if (isSms) {
      return (
        <span style={{
          fontSize: 11, borderRadius: 12, padding: "2px 10px",
          background: "#dcfce7", color: "#166534", border: "1px solid #86efac",
          whiteSpace: "nowrap",
        }}>sms</span>
      );
    }
    if (isPlatform) {
      return (
        <span style={{
          fontSize: 11, borderRadius: 12, padding: "2px 10px",
          background: "#f3e8ff", color: "#7c3aed", border: "1px solid #c4b5fd",
          whiteSpace: "nowrap",
        }}>platform</span>
      );
    }
    return tag ? (
      <span style={{
        fontSize: 11, borderRadius: 12, padding: "2px 10px",
        background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0",
        whiteSpace: "nowrap",
      }}>{tag}</span>
    ) : "—";
  })();

  if (isMobile) {
    return (
      <div style={{
        padding: "14px 16px",
        borderBottom: isLast ? "none" : "1px solid #f3f4f6",
        overflow: "hidden", minWidth: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
          <span title={cfg.desc || ""} style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            borderRadius: 14, padding: "3px 10px", fontWeight: 600, fontSize: 11,
            background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
            cursor: cfg.desc ? "help" : "default",
          }}>
            <cfg.Icon style={{ fontSize: 10 }} /> {cfg.label}
          </span>
          {tagChip !== "—" && tagChip}
        </div>
        {isPlatform && row.detail && (
          <div style={{ fontSize: 12, color: "#374151", marginBottom: 2 }}>{row.detail}</div>
        )}
        {isPlatform && row.page_url && (() => {
          try {
            const u = new URL(row.page_url);
            const slug = u.pathname.split('/').filter(Boolean).pop() || u.pathname;
            return <a href={row.page_url} target="_blank" rel="noopener noreferrer" title={u.pathname}
              style={{ fontSize: 11, color: "#9ca3af", textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }}>
              {slug}
            </a>;
          } catch { return null; }
        })()}
        {!isPlatform && row.subject && (
          <div style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2, maxWidth: "100%" }}>
            {row.subject}
          </div>
        )}
        {!isPlatform && row.clicked_url && (
          <a href={row.clicked_url} target="_blank" rel="noopener noreferrer" title={row.clicked_url}
            style={{ fontSize: 11, color: "#4338ca", textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2, maxWidth: "100%" }}>
            {row.clicked_url}
          </a>
        )}
        {!isPlatform && row.event_type === "Click" && row.country && (
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }} title="Click origin country (Postmark Geo)">
            🌍 {row.country}
          </div>
        )}
        {!isPlatform && !isSms && row.message_id && (
          <a
            href={`/email-details/${encodeURIComponent(row.message_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, color: "#0369a1", textDecoration: "none", display: "inline-block", marginBottom: 2 }}
          >
            View email →
          </a>
        )}
        <div style={{ fontSize: 11, color: "#9ca3af" }}>
          {formatTimestamp(row.created_at)} {formatTime(row.created_at)}
          {relative && <span style={{ marginLeft: 6 }}>({relative})</span>}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "150px 1fr 130px 160px",
        padding: "12px 20px",
        borderBottom: isLast ? "none" : "1px solid #f3f4f6",
        alignItems: "center",
        fontSize: 13,
        transition: "background 0.1s",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "#fafbfc"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
    >
      <div>
        <span title={cfg.desc || ""} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          borderRadius: 14, padding: "3px 10px", fontWeight: 600, fontSize: 11,
          background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
          whiteSpace: "nowrap", cursor: cfg.desc ? "help" : "default",
        }}>
          <cfg.Icon style={{ fontSize: 10 }} /> {cfg.label}
        </span>
      </div>

      {/* Detail / subject */}
      <div style={{ overflow: "hidden", textOverflow: "ellipsis", color: "#6b7280", paddingRight: 12, minWidth: 0 }}>
        {isPlatform ? (
          <>
            {row.detail && <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#374151" }}>{row.detail}</div>}
            {row.page_url && (() => {
              try {
                const u = new URL(row.page_url);
                const slug = u.pathname.split('/').filter(Boolean).pop() || u.pathname;
                return <a href={row.page_url} target="_blank" rel="noopener noreferrer" title={u.pathname}
                  style={{ fontSize: 11, color: "#9ca3af", textDecoration: "none", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {slug}
                </a>;
              } catch { return null; }
            })()}
            {!row.detail && !row.page_url && (
              <span>{PLATFORM_EVENT_CONFIG[row.event_type]?.label || row.event_type}</span>
            )}
          </>
        ) : (
          <>
            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.subject || "\u2014"}
            </div>
            {row.clicked_url && (
              <a href={row.clicked_url} target="_blank" rel="noopener noreferrer" title={row.clicked_url}
                style={{ fontSize: 11, color: "#4338ca", textDecoration: "none", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.clicked_url}
              </a>
            )}
            {row.event_type === "Click" && row.country && (
              <div style={{ fontSize: 11, color: "#6b7280" }} title="Click origin country (Postmark Geo)">
                🌍 {row.country}
              </div>
            )}
            {!isSms && row.message_id && (
              <a
                href={`/email-details/${encodeURIComponent(row.message_id)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{ fontSize: 11, color: "#0369a1", textDecoration: "none", display: "inline-block" }}
              >
                View email →
              </a>
            )}
          </>
        )}
      </div>

      <div>{tagChip}</div>

      <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <div style={{ color: "#374151", fontSize: 12 }}>
          {formatTimestamp(row.created_at)}, {formatTime(row.created_at)}
        </div>
        {relative && <div style={{ fontSize: 11, color: "#9ca3af" }}>{relative}</div>}
      </div>
    </div>
  );
}
